import React from 'react';
import { Filter, SortAsc, SortDesc, X, GraduationCap, Globe, Building2 } from 'lucide-react';

const ResourceFilters = ({ 
  filters, 
  subjects, 
  universities = [],
  userUniversity,
  onFiltersChange, 
  onReset,
  resourceCount = 0,
  getUniversityFilterDescription
}) => {
  const resourceTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'video', label: 'Videos' },
    { value: 'pdf', label: 'PDFs' },
    { value: 'document', label: 'Documents' },
    { value: 'past papers', label: 'Past Papers' },
    { value: 'link', label: 'Links' }
  ];

  const universityFilterOptions = [
    { 
      value: 'smart', 
      label: 'Smart Filter', 
      icon: GraduationCap,
      description: userUniversity ? `Show relevant resources for ${userUniversity}` : 'Show all resources'
    },
    { 
      value: 'my-university', 
      label: 'My University', 
      icon: Building2,
      description: userUniversity ? `Show resources from ${userUniversity} only` : 'Show university-specific resources'
    },
    { 
      value: 'all-universities', 
      label: 'All Universities', 
      icon: Globe,
      description: 'Show resources available to all universities'
    },
    { 
      value: 'all', 
      label: 'Everything', 
      icon: Filter,
      description: 'Show all resources regardless of university'
    }
  ];

  const sortOptions = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'likes', label: 'Most Liked' },
    { value: 'views', label: 'Most Viewed' },
    { value: 'title', label: 'Title (A-Z)' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({ [key]: value });
  };

  const handleSortOrderToggle = () => {
    const newOrder = filters.sortOrder === 'desc' ? 'asc' : 'desc';
    onFiltersChange({ sortOrder: newOrder });
  };

  const hasActiveFilters = () => {
    return filters.subject !== 'all' || 
           filters.resourceType !== 'all' || 
           filters.universityFilter !== 'smart' ||
           filters.sortBy !== 'createdAt' || 
           filters.sortOrder !== 'desc';
  };

  const getUniversityFilterLabel = (value) => {
    const option = universityFilterOptions.find(opt => opt.value === value);
    return option ? option.label : 'Smart Filter';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="w-5 h-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <span className="ml-2 bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded-full">
            {resourceCount} resource{resourceCount !== 1 ? 's' : ''}
          </span>
        </div>
        
        {hasActiveFilters() && (
          <button
            onClick={onReset}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-4 h-4 mr-1" />
            Reset Filters
          </button>
        )}
      </div>

      {/* University Filter Description */}
      {getUniversityFilterDescription && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center text-sm text-blue-700">
            <GraduationCap className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>{getUniversityFilterDescription()}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* University Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            University Scope
          </label>
          <select
            value={filters.universityFilter}
            onChange={(e) => handleFilterChange('universityFilter', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {universityFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Subject Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <select
            value={filters.subject}
            onChange={(e) => handleFilterChange('subject', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>

        {/* Resource Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Resource Type
          </label>
          <select
            value={filters.resourceType}
            onChange={(e) => handleFilterChange('resourceType', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {resourceTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort By
          </label>
          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort Order */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort Order
          </label>
          <button
            onClick={handleSortOrderToggle}
            className="w-full flex items-center justify-center border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
          >
            {filters.sortOrder === 'desc' ? (
              <>
                <SortDesc className="w-4 h-4 mr-2" />
                Descending
              </>
            ) : (
              <>
                <SortAsc className="w-4 h-4 mr-2" />
                Ascending
              </>
            )}
          </button>
        </div>
      </div>

      {/* University Filter Options - Visual Guide */}
      {filters.universityFilter !== 'smart' && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Filter Options:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {universityFilterOptions.map((option) => {
              const IconComponent = option.icon;
              const isActive = filters.universityFilter === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('universityFilter', option.value)}
                  className={`p-2 text-left border rounded-lg transition-colors text-sm ${
                    isActive
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    <IconComponent className={`w-3 h-3 mr-1 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium">{option.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-tight">
                    {option.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600 mr-2">Active filters:</span>
            
            {filters.universityFilter !== 'smart' && (
              <span className="inline-flex items-center bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                University: {getUniversityFilterLabel(filters.universityFilter)}
                <button
                  onClick={() => handleFilterChange('universityFilter', 'smart')}
                  className="ml-1 hover:text-indigo-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filters.subject !== 'all' && (
              <span className="inline-flex items-center bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                Subject: {filters.subject}
                <button
                  onClick={() => handleFilterChange('subject', 'all')}
                  className="ml-1 hover:text-green-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filters.resourceType !== 'all' && (
              <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                Type: {resourceTypes.find(t => t.value === filters.resourceType)?.label}
                <button
                  onClick={() => handleFilterChange('resourceType', 'all')}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {(filters.sortBy !== 'createdAt' || filters.sortOrder !== 'desc') && (
              <span className="inline-flex items-center bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                Sort: {sortOptions.find(s => s.value === filters.sortBy)?.label} ({filters.sortOrder})
                <button
                  onClick={() => onFiltersChange({ sortBy: 'createdAt', sortOrder: 'desc' })}
                  className="ml-1 hover:text-purple-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceFilters;